import { type ForwardedRef, forwardRef, useMemo } from 'react';

import { EmergencyContactRelation } from '@/common/graphql';
import { EmergencyContactRelationLabels } from '@/common/types';
import { ObjectUtils } from '@/common/utils';

import type { ComboboxItemWithFormatted } from '../props';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

const order = [
  EmergencyContactRelation.Mother,
  EmergencyContactRelation.Father,
  EmergencyContactRelation.Wife,
  EmergencyContactRelation.Husband,
  EmergencyContactRelation.Brother,
  EmergencyContactRelation.Sister,
  EmergencyContactRelation.Other,
];

const data = ObjectUtils.arrayFromEnumValues<EmergencyContactRelation>(EmergencyContactRelation)
  .map(value => ({
    label: EmergencyContactRelationLabels[value],
    value,
  }))
  .sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));

interface EmergencyContactRelationSelectSingleProps
  extends Omit<SelectSingleProps<ComboboxItemWithFormatted<EmergencyContactRelation>>, 'searchable' | 'value'> {
  overrideLabel?: (value: EmergencyContactRelation) => string | false;
  value: EmergencyContactRelation | null;
}

export const EmergencyContactRelationSelectSingle = forwardRef(
  (
    { placeholder = 'Select relation', overrideLabel, ...rest }: EmergencyContactRelationSelectSingleProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const overriddenData = useMemo(
      () =>
        data.map(it => ({
          ...it,
          label: overrideLabel?.(it.value) || it.label,
        })),
      [overrideLabel],
    );

    return (
      <SelectSingle
        ref={ref}
        {...rest}
        data={overriddenData}
        loading={false}
        maxItemVisible={overriddenData.length}
        placeholder={placeholder}
      />
    );
  },
);
